// ── Related config files, inline-editable ────────────────────────────────
//
// Pinned at the bottom of every real node's drawer. Contents live on
// `wf.configFiles[taskId]`, seeded on first render from `defaultConfigFiles`
// so a freshly-added node still has something to show/edit.
import { useState } from 'react'
import { ChevronDown, ChevronRight, FileCode2 } from 'lucide-react'
import { useOpsCenterStore } from '../../store'
import { RELATED_CONFIG_FILES, defaultConfigFiles } from '../../data'
import type { ConfigFile, StepName, Workflow } from '../../types'

export interface ConfigFilesSectionProps {
  nodeKey: StepName | 'WEBHOOK'
  wf: Workflow
  taskId: string
}

export function ConfigFilesSection({ nodeKey, wf, taskId }: ConfigFilesSectionProps) {
  const updateWorkflow = useOpsCenterStore((s) => s.updateWorkflow)
  const [openFile, setOpenFile] = useState<string | null>(null)

  const names = RELATED_CONFIG_FILES[nodeKey] ?? []
  const files = wf.configFiles?.[taskId] ?? defaultConfigFiles(names)

  function updateContent(name: string, content: string) {
    const next: ConfigFile[] = files.some((f) => f.name === name)
      ? files.map((f) => (f.name === name ? { ...f, content } : f))
      : [...files, { name, content }]
    updateWorkflow(wf.id, { configFiles: { ...(wf.configFiles ?? {}), [taskId]: next } })
  }

  if (names.length === 0) return null

  return (
    <div className="p-3">
      <p className="text-[10px] uppercase tracking-wide text-[#5b6b85] mb-2">Related config files</p>
      <div className="flex flex-col gap-1.5">
        {names.map((name) => {
          const file = files.find((f) => f.name === name) ?? { name, content: '' }
          const isOpen = openFile === name
          return (
            <div key={name} className="rounded-md border border-[#1e2c44] bg-[#0b1220] overflow-hidden">
              <button
                onClick={() => setOpenFile(isOpen ? null : name)}
                className="w-full flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-[var(--text-primary)] hover:bg-[#1a2740] transition-colors"
              >
                {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                <FileCode2 size={12} className="text-[#5b6b85] shrink-0" />
                <span className="truncate">{name}</span>
              </button>
              {isOpen && (
                <textarea
                  value={file.content}
                  onChange={(e) => updateContent(name, e.target.value)}
                  spellCheck={false}
                  rows={6}
                  className="w-full bg-[#0a1322] border-t border-[#1e2c44] px-2.5 py-2 text-[10px] font-mono text-[var(--text-primary)] focus:outline-none resize-y"
                />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
